import { CardActions, CardContent } from '@mui/material';
import { DocumentColumns } from '../enhanced/common-headers/documentsLM';
import { GridColDef } from '@mui/x-data-grid-premium';
import toast from 'features/toast';
import SectionTitle from '../SectionTitle';
import { EnhancedDataGrid } from '../enhanced/data-grid';
import { AddDocumentDialog } from './AddDocumentDialog';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';

import { fmDocumentService } from 'services/api/documents/FMDocumentService';

const MAX_NUMBER_TAGS = 999;

interface IDocumentTableCardComponentParams {
  originalService: string; // The this string will be APPENDED at the URL
  objectId: number; // The object ID to be used while linking the document to the object
  dictParams: any;
  folder: string;
  folder_description: string;
  isFornitore?: boolean;
  disableEdit?: boolean;
}

export const DocumentTableCardComponent = (params: IDocumentTableCardComponentParams) => {
  const {
    originalService,
    dictParams,
    objectId,
    folder,
    folder_description,
    isFornitore,
    disableEdit
  } = params;
  const { t } = useTranslation();

  const refresh = useRef(null);
  /**
   * Function to handle the download of a document.
   * If the document ID is provided, download the attachment.
   *
   * @param {Object} document - The document object with the ID.
   */
  const handleDownloadDocument = (document) => {
    if (document?.id) {
      fmDocumentService.downloadLmAttachment(originalService, document.id);
    }
  };

  /**
   * Function to handle the deletion of a document.
   * If the document ID is provided, delete the document.
   *
   * @param {Object} document - The document object with the ID.
   */
  const handleDeleteDocument = (documentId) => {
    if (documentId) {
      fmDocumentService.delete(documentId).then(() => {
        toast.success(t('entities.document.delete.success'));
        refresh.current();
      });
    }
  };

  /**
   * Columns for the document grid.
   * Includes a download and delete button for each document.
   */
  const documentColumns: GridColDef[] = DocumentColumns(
    handleDownloadDocument,
    handleDeleteDocument
  );

  const onSubmit = (values, suppliers) => {
    const selectedSupplier = values.supplier
      ? suppliers.find((supplier) => supplier.id === values.supplier)
      : undefined;

    fmDocumentService
      .uploadDocument(
        values.tipology,
        values.attachment.item(0),
        values.name,
        values.description || '',
        folder,
        folder_description +
          (selectedSupplier ? `Fornitore: ${selectedSupplier.label}` : '') +
          (values.tipology && typeof values.tipology === 'object' && values.tipology.name
            ? `Tipologia: ${values.tipology.name}`
            : '') +
          (values.category && values.category.name ? `Categoria: ${values.category.name}` : ''),
        values.category || '',
        values.supplier,
        values.warehouse,
        originalService,
        [objectId]
      )
      .then((res) => {
        if (res) {
          toast.success(t('entities.document.upload-success'));
        } else {
          toast.error(t('entities.document.upload-error'));
        }
        refresh.current();
      });
  };

  const getDocuments = async (
    limit: number,
    offset: number,
    params: Record<string, string> = {}
  ) => {
    const mergedParams = {
      ...dictParams
      // ...params // i documenti sono già filtrati via dictParams, l'aggiunta dei Warehouse "nasconderebbe" troppo
    };
    const result = await fmDocumentService.getAllPaginatedDocuments(
      originalService,
      limit,
      offset,
      mergedParams
    );
    return result;
  };

  return (
    <>
      <div>
        <CardContent>
          <div className="flex flex-wrap">
            <div className="flex w-full justify-between">
              <SectionTitle>{t(`global.documents-storage`)}</SectionTitle>
              {!disableEdit && (
                <CardActions>
                  <AddDocumentDialog
                    onSubmit={onSubmit}
                    originalService={originalService}
                    objectId={objectId}
                    isFornitore={isFornitore}
                  />
                </CardActions>
              )}
            </div>
            <br />
            {/* Display the grid with the documents */}
            <EnhancedDataGrid
              columns={documentColumns}
              service={fmDocumentService}
              getMethod={getDocuments}
              outerParams={dictParams}
              refresh={refresh}
              disableEdit={disableEdit}
            />
          </div>
        </CardContent>
      </div>
    </>
  );
};
